import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import {
  getSubmissionOutcome,
  isClaimBlockedByMissingPpsn,
} from "utils/immunizationStatus";
import { showMutationError } from "utils/showMutationError";

import { getRouteCode } from "../utils/routeCode";

/**
 * Send one vaccination to Healthlink, and say honestly what came back.
 *
 * Shared by the all-patient list and the patient's own immunisations table. A saved
 * vaccination is submitted automatically, so this is the retry path: it exists for the
 * records where that automatic submission did not arrive (KIN-1820).
 *
 * A 200 does not mean both messages went. `healthlinkResponse` says which did, and one
 * of its values — no reimbursement claim sent because the patient has no PPSN — is an
 * unpaid dose that needs a person, so it must not read as success (KIN-1786/1789).
 *
 * The request is synchronous and slow: the backend polls for the NIIS acknowledgement
 * ten times three seconds apart, so `isSubmitting` is what stops a clinician pressing
 * the action again while the first attempt is still in flight.
 */
export const useSubmitVaccineToHealthlink = ({ tenantVal, onSubmitted }) => {
  const [ppsnBlockedPatientId, setPpsnBlockedPatientId] = useState(null);

  const { mutate: submitVaccine, isPending: isSubmitting } = useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async ({ immunisationId, administrationMethod }) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/healthlink/vaccine/submit/${immunisationId}`,
        { routeCode: getRouteCode(administrationMethod) }
      );

      return response.data;
    },
    onSuccess: (data, variables) => {
      const healthlinkResponse = data?.healthlinkResponse;
      const { severity, message } = getSubmissionOutcome(healthlinkResponse);

      if (isClaimBlockedByMissingPpsn(healthlinkResponse)) {
        setPpsnBlockedPatientId(variables.patientId ?? null);
      } else if (severity === "success") {
        toast.success(message);
      } else {
        toast(message, {
          id: `vaccine-submit-${variables.immunisationId}`,
          duration: 10000,
        });
      }

      onSubmitted?.();
    },
    onError: (error) =>
      showMutationError(error, "Failed to submit vaccine to Healthlink"),
  });

  return {
    submitVaccine,
    isSubmitting,
    ppsnBlockedPatientId,
    clearPpsnBlocked: () => setPpsnBlockedPatientId(null),
  };
};
